import React, {Dispatch, SetStateAction, useState} from 'react';
import {gql, useQuery} from '@apollo/client';
import {Root as DialogRoot} from '@radix-ui/react-dialog';
import Image from 'next/image';

import {ProductVariant, SubscriptionItem} from '#apollo/schema';
import {
  UpdateSubscriptionInput,
  useCreateAndApplyDiscount,
  useUpdateSubscriptionMutation,
} from '#apollo/schema';
import {Button, Icon, Modal, Text} from '#component/base';
import {UncertainProgress} from '#component/base';
import {ModalClose} from '#component/base/Modal';
import {managePlanQuery} from '#component/page/Profile/components/ManagePlanTab/useManagePlanTab';
import {PauseSubscriptionQuery} from '#component/page/Profile/components/SubscriptionTab/CTASubscription/SkipNextOrderModal/usePauseSubscriptionItems';
import {profileUpcomingOrderQuery} from '#component/page/Profile/components/SubscriptionTab/useSubscriptionTab';
import {EventType, useAnalytics, UserActionEvent} from '#component/provider/AnalyticsProvider';
import {csEmail} from '#constant/information';
import {
  AMPLIFY_TID,
  DEFEND_TID,
  MEMBER_TID,
  NAD_TID,
  RESTORE_TID,
  SHARPEN_TID,
  VITALITY_TID,
} from '#constant/products';
import {useToastQueue, useViewportSize} from '#hook';
import {text} from '#theme/text';
import {
  getProductIdsByProductTids,
  getProductTidsByProductIds,
  hasSubscriptionItem,
} from '#util/subscriptions';

import {SkipNextOrderModal} from '../../../SubscriptionTab/CTASubscription/SkipNextOrderModal';

import * as css from './CancelationModal.css';

const memberProduct = String(MEMBER_TID);
const vitalityProduct = String(VITALITY_TID);
const amplifyProduct = String(AMPLIFY_TID);
const sharpenProduct = String(SHARPEN_TID);
const defendProduct = String(DEFEND_TID);
const restoreProduct = String(RESTORE_TID);
const nadProduct = String(NAD_TID);

const benefits = {
  [memberProduct]: 'Access to regular testing and personalized action plans',
  [vitalityProduct]: 'Slower cellular aging with Vitality',
  [amplifyProduct]: 'Boosted metabolism and energy with Amplify',
  [restoreProduct]: 'Better sleep with Restore',
  [defendProduct]: 'Immunity & inflammaging support with Defend',
  [sharpenProduct]: 'Enhanced focus, clarity and memory with Sharpen',
  [nadProduct]: 'Amplified mitochondrial function and skin health with NAD+',
  SUPP: `Up to 25% off Tally's science-backed supplements`,
};

const cancelationReasons = [
  {
    id: 'supplement_side_effects',
    text: 'Supplement side effects',
    image: '/image/winback-discount/supplement-side-effects.jpg',
    mobileImage: '/image/winback-discount/supplement-side-effects-mobile.jpg',
    description:
      'Try another month to reach your goals —ramping up to the full dosage can make all the difference.',
  },
  {
    id: 'order_as_needed',
    text: 'Prefer to order supplements as needed',
    image: '/image/winback-discount/order-as-needed.jpg',
    mobileImage: '/image/winback-discount/order-as-needed-mobile.jpg',
    description: 'Overstocked? On the go? Commit to consistency and keep your progress on track.',
  },
  {
    id: 'budgetary_reason',
    text: 'Budgetary reasons',
    image: '/image/winback-discount/budgetary.jpg',
    mobileImage: '/image/winback-discount/budgetary-mobile.jpg',
    description:
      'Your health is worth it. Invest in you with an exclusive discount on next month’s order.',
  },
  {
    id: 'supplement_surplus',
    text: 'Supplement surplus',
    image: '/image/winback-discount/surplus.jpg',
    mobileImage: '/image/winback-discount/surplus-mobile.jpg',
    description: 'Overstocked? On the go? Commit to consistency and keep your progress on track.',
  },
  {
    id: 'not_satisfied',
    text: 'Not satisfied',
    image: '/image/winback-discount/not-satisfied.jpg',
    mobileImage: '/image/winback-discount/not-satisfied-mobile.jpg',
    description:
      'Not satisfied yet? Change your subscription any time and find the stack that works for you.',
  },
  {
    id: 'other',
    text: 'Other',
    image: '/image/winback-discount/other.jpg',
    mobileImage: '/image/winback-discount/other-mobile.jpg',
    description:
      'Invest in tomorrow, starting today. Unlock your exclusive discount and experience the future of healthy aging.',
  },
];

export const createAndApplyDiscountMutation = gql`
  mutation CreateAndApplyDiscount($createAndApplyDiscountInput: CreateAndApplyDiscountInput!) {
    createAndApplyDiscount(input: $createAndApplyDiscountInput) {
      id
    }
  }
`;

export const updateSubscriptionStatusMutation = gql`
  mutation UpdateSubscriptionStatus($subscriptionChanges: UpdateSubscriptionInput!) {
    updateSubscription(subscriptionChanges: $subscriptionChanges) {
      id
    }
  }
`;

interface CancelationModalProps {
  open: boolean;
  onOpenChange: Dispatch<SetStateAction<boolean>>;
  productVariants: ProductVariant[];
  hasAppliedDiscountCancelation: boolean;
  subscriptionItems: SubscriptionItem[];
  subscriptionEmail: string;
}

export function CancelationModal({
  open,
  onOpenChange,
  productVariants,
  hasAppliedDiscountCancelation,
  subscriptionItems,
  subscriptionEmail,
}: CancelationModalProps) {
  const {track} = useAnalytics();
  const {isMobile, isTablet} = useViewportSize();
  const [selectedReason, setSelectedReason] = useState<string | null>(null);
  const [showStepTwo, setShowStepTwo] = useState(false);
  const [showStepThree, setShowStepThree] = useState(false);
  const [hasCreateAndApplyDiscountError, setCreateAndApplyDiscountError] = useState(false);
  const [hasCancelledSubscriptionError, setCancelledSubscriptionError] = useState(false);

  const {addToast} = useToastQueue();

  const [updateSubscriptionStatus, {loading: statusUpdateLoading}] =
    useUpdateSubscriptionMutation();
  const {refetch: refetchManagePlan} = useQuery(managePlanQuery);

  const [createAndApplyWinbackDiscount, {loading}] = useCreateAndApplyDiscount({
    refetchQueries: [
      {query: managePlanQuery},
      {query: PauseSubscriptionQuery},
      {query: profileUpcomingOrderQuery},
    ],
  });

  const handleReturnToManagePlan = () => {
    onOpenChange(false);
  };

  const handleCancelPlanClick = () => {
    track<UserActionEvent>({
      evtName: EventType.UserAction,
      actionName: 'Selected cancellation reason',
      data: {selectedReason: selectedReason || 'Unspecified'},
    });
    if (hasAppliedDiscountCancelation) {
      setShowStepThree(true);
    } else {
      setShowStepTwo(true);
    }
  };

  const handleWinbackImageClick = async () => {
    const input = {
      discountCategory: 'CANCELATION',
      discountValueType: 'PERCENTAGE',
      discountValue: 0.3,
      discountLevel: 'ORDER',
    };

    try {
      await createAndApplyWinbackDiscount({
        variables: {
          createAndApplyDiscountInput: input,
        },
      });

      onOpenChange(false);

      track<UserActionEvent>({
        evtName: EventType.UserAction,
        actionName: 'Claimed winback offer',
      });

      addToast({
        children: 'Your discount has been applied for your next order.',
      });
    } catch {
      setCreateAndApplyDiscountError(true);
    }
  };

  const handleChatWithUs = () => {
    track<UserActionEvent>({
      evtName: EventType.UserAction,
      actionName: 'Cancelation chat with us',
    });
    window.location.href = process.env.NEXT_PUBLIC_CONTACT_URL || '';
  };

  const handleConfirmCancellation = () => {
    setShowStepThree(true);
    setShowStepTwo(false);
  };

  const handleCancellation = async () => {
    const subscriptionChanges: UpdateSubscriptionInput = {
      updateStatusTo: 'CANCELLED',
    };

    try {
      await updateSubscriptionStatus({
        variables: {
          subscriptionChanges,
        },
      });

      onOpenChange(false);

      track<UserActionEvent>({
        evtName: EventType.UserAction,
        actionName: 'Confirmed cancellation',
        data: {
          email: subscriptionEmail,
        },
      });

      await refetchManagePlan();

      addToast({
        children: 'Your subscription has been cancelled.',
      });
    } catch {
      setCancelledSubscriptionError(true);
    }
  };

  let benefitsList: string[] = [];

  const itemProductTids = [
    vitalityProduct,
    amplifyProduct,
    sharpenProduct,
    defendProduct,
    restoreProduct,
    nadProduct,
  ];

  const itemProductIds = getProductIdsByProductTids(productVariants, itemProductTids);

  for (const item of itemProductIds) {
    const hasItem = hasSubscriptionItem(subscriptionItems, [item]);
    if (hasItem) {
      const productTid = getProductTidsByProductIds(productVariants, [item])[0];
      benefitsList.push(benefits[productTid]);
    }
  }

  const memberProductId = getProductIdsByProductTids(productVariants, [memberProduct]);
  const hasMember = hasSubscriptionItem(subscriptionItems, memberProductId);

  if (hasMember) {
    benefitsList.push(benefits[memberProduct]);
  } else {
    benefitsList.push(benefits.SUPP);
  }

  if (benefitsList.length > 3) {
    benefitsList = benefitsList.slice(0, 3);
  }

  const renderStepThree = () => {
    return (
      <div>
        <div className={css.header}>
          <Text variant="heading3" color="deepBurgundy" className={css.HeaderText}>
            {statusUpdateLoading ? 'Cancelling Plan' : 'Cancel Plan'}
          </Text>
          <Button icon="X" as={ModalClose} variant="secondary" onClick={handleReturnToManagePlan} />
        </div>
        {!statusUpdateLoading && hasCancelledSubscriptionError && (
          <div className={css.ErrorMessageWrapper}>
            <Text as="div" variant="body" color="doveGray">
              Sorry, we cannot cancel your plan at this time.
              <br />
              <br />
              Please reach out to us at{' '}
              <a className={css.MailPrivacy} href={`mailto:${csEmail}`}>
                {csEmail + ' '}
              </a>
            </Text>
            <Button onClick={handleReturnToManagePlan}>Return to Manage My Plan</Button>
          </div>
        )}
        {statusUpdateLoading ? (
          <div className={css.LoadingStyle}>
            <UncertainProgress width={144} size={24} duration={1} />
          </div>
        ) : (
          <>
            <>
              <div className={css.Section}>
                <div className={css.ChatWithUsWrapper}>
                  <div className={css.ChatWithUsText}>
                    <Text variant="buttonSmall" color="deepBurgundy">
                      How can we help?
                    </Text>
                    <Text
                      as="div"
                      variant="buttonSmall"
                      color="doveGray"
                      className={css.InformationText}>
                      <p>
                        Our team would be happy to connect to better understand your experience.
                      </p>
                    </Text>
                  </div>
                  <Image src="/image/chat-with-us.png" alt="chat with us" width={80} height={80} />
                </div>
                <Button variant="dark" style={{height: '45px'}} onClick={handleChatWithUs}>
                  Chat With Us
                </Button>
              </div>
              <Text as="div" variant="body" color="doveGray" className={css.TextBottom}>
                <p>Otherwise, confirm your cancellation below.</p>
              </Text>
              <Button
                variant="secondary"
                className={css.CancellationButtonSizing}
                onClick={handleCancellation}>
                Confirm Cancellation
              </Button>
            </>
          </>
        )}
      </div>
    );
  };

  const renderStepTwo = () => {
    const selected =
      cancelationReasons.find(reason => reason.id === (selectedReason || 'other')) ||
      cancelationReasons[-1];
    return (
      <div>
        <div className={css.header}>
          <Text variant="heading3" color="deepBurgundy" className={css.HeaderText}>
            {loading || !loading ? 'Updating Plan' : 'Cancel Plan'}
          </Text>
          <Button icon="X" as={ModalClose} variant="secondary" />
        </div>
        {!loading && hasCreateAndApplyDiscountError && (
          <div className={css.ErrorMessageWrapper}>
            <Text as="div" variant="body" color="doveGray">
              Sorry, we cannot apply this promo to your plan.
              <br />
              <br />
              Please reach out to us at{' '}
              <a className={css.MailPrivacy} href={`mailto:${csEmail}`}>
                {csEmail + ' '}
              </a>
            </Text>
            <Button onClick={handleReturnToManagePlan}>Return to Manage My Plan</Button>
          </div>
        )}
        <div>
          {loading ? (
            <div className={css.LoadingStyle}>
              <UncertainProgress width={144} size={24} duration={1} />
            </div>
          ) : (
            <>
              {!hasCreateAndApplyDiscountError && (
                <>
                  <img
                    src={!isMobile && !isTablet ? selected.image : selected.mobileImage}
                    alt={selected.text}
                    className={css.WinbackImage}
                    onClick={handleWinbackImageClick}
                    style={{cursor: 'pointer'}}
                  />
                  <Text as="div" variant="body" color="doveGray" className={css.TextBottom}>
                    <p>Otherwise you can continue with cancelling your plan by confirming below.</p>
                  </Text>
                  <Button
                    variant="secondary"
                    className={css.CancellationButtonSizing}
                    onClick={handleConfirmCancellation}>
                    Confirm Cancellation
                  </Button>
                </>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  const renderContent = () => {
    if (showStepThree) {
      return renderStepThree();
    }

    if (showStepTwo && !hasAppliedDiscountCancelation) {
      return renderStepTwo();
    }

    // Step One
    return (
      <>
        <div className={css.header}>
          <Text variant="heading3" color="burgundy">
            Cancel Plan
          </Text>
          <Button icon="X" as={ModalClose} variant="secondary" />
        </div>
        <div className={!isMobile && !isTablet ? css.ModalContainer : ''}>
          <div className={!isMobile && !isTablet ? css.BenefitsSection : ''}>
            <div className={css.Section}>
              <Text variant="buttonSmall" color="deepBurgundy">
                Benefits you&apos;ll miss out on
              </Text>
              <ul className={css.BelowTextSpacing}>
                {benefitsList.map(benefit => (
                  <li className={css.BenefitWrapper} key={benefit}>
                    <div className={css.IconContainer}>
                      <Icon
                        aria-hidden
                        name="Check"
                        size="normal"
                        color="solidGreen"
                        weight="bold"
                      />
                    </div>
                    <Text className={css.BenefitLabel} color="doveGray">
                      {benefit}
                    </Text>
                  </li>
                ))}
              </ul>
              <Button onClick={handleReturnToManagePlan} variant="dark" style={{height: '37px'}}>
                Keep My Benefits
              </Button>
            </div>
          </div>
          <div className={!isMobile && !isTablet ? css.SkipSection : ''}>
            <div className={css.Section}>
              <div className={css.BelowTextSpacing}>
                <Text variant="buttonSmall" color="deepBurgundy">
                  Want to skip a month?
                </Text>
                <div className={css.InformationWrapper}>
                  <Text
                    as="div"
                    variant="buttonSmall"
                    color="doveGray"
                    className={css.InformationText}>
                    <p>
                      Have extra supplements? Traveling? Skip next month and keep your benefits.
                    </p>
                  </Text>
                  <Image
                    src="/image/fast-forward.png"
                    alt="skip next order"
                    width={55}
                    height={55}
                  />
                </div>
              </div>
              <SkipNextOrderModal
                productVariants={productVariants}
                skipBtnVariant="dark"
                buttonLabel="Skip Next Month"
                size="narrow"
              />
            </div>
            {(isMobile || isTablet) && (
              <Text as="div" variant="caption" color="doveGray" className={css.SkipSectionCaption}>
                Need to pause your plan for longer than one month? Please reach out to Customer
                Success at{' '}
                <a className={css.MailPrivacy} href={`mailto:${csEmail}`}>
                  {csEmail + ' '}
                </a>
                for assistance.
              </Text>
            )}
          </div>
          <div className={!isMobile && !isTablet ? css.ReasonsSection : ''}>
            <div className={css.CancelationReasonsSection}>
              <Text
                as="div"
                variant="buttonSmall"
                color="doveGray"
                style={text.body.small as unknown as React.CSSProperties}>
                <p>Tell us why you want to cancel your plan</p>
              </Text>
              {cancelationReasons.map(reason => (
                <label key={reason.id} className={css.CancelationSelection}>
                  <input
                    type="radio"
                    name="cancelationReason"
                    value={reason.id}
                    className={css.CancelationButton}
                    checked={selectedReason === reason.id}
                    onChange={() => setSelectedReason(reason.id)}
                  />
                  {reason.text}
                </label>
              ))}
              <Button
                as="a"
                variant="secondary"
                className={css.CancellationButtonSizing}
                onClick={handleCancelPlanClick}>
                Cancel Plan
              </Button>
            </div>
          </div>
          {!isMobile && !isTablet && (
            <Text as="div" variant="caption" color="doveGray" className={css.SkipSectionCaption}>
              Need to pause your plan for longer than one month? Please reach out to Customer
              Success at{' '}
              <a className={css.MailPrivacy} href={`mailto:${csEmail}`}>
                {csEmail + ' '}
              </a>
              for assistance.
            </Text>
          )}
        </div>
      </>
    );
  };

  return (
    <>
      <DialogRoot open={open} onOpenChange={onOpenChange}>
        <Modal layout="wideDrawer">
          <div className={css.root}>{renderContent()}</div>
        </Modal>
      </DialogRoot>
    </>
  );
}
